import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { tap } from 'rxjs/operators';
import { AnalyticalPlanInitAnalyticalParametersAction } from 'src/app/_shared/stores/analytical-plan/analytical-plan.actions';
import { AnalyticalPlanAndSections } from 'src/app/_shared/stores/analytical-plan/analytical-plan.state';

@Component({
    selector: 'app-shared-analytical-plan-picker',
    templateUrl: './shared-analytical-plan-picker.component.html',
    styleUrls: ['./shared-analytical-plan-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAnalyticalPlanPickerComponent),
    standalone: false
})
export class SharedAnalyticalPlanPickerComponent extends CustomFormControlAccess implements OnInit {

    _loading = false;

    _analyticalPlanId$ = new BehaviorSubject<number>(null);

    @Input() placeholder: string = null;

    @Input() label: string = null;

    @Coerce('boolean')
    @Input()
    required: boolean;

    formControl = new UntypedFormControl();

    private analyticalPlansAndSections: AnalyticalPlanAndSections[] = [];

    planAndSections: AnalyticalPlanAndSections = null;

    constructor(private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly fb: UntypedFormBuilder,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    @Input()
    set analyticalPlanId(value: number) {
        this._analyticalPlanId$.next(value);
    }

    get analyticalPlanId(): number {
        return this._analyticalPlanId$.getValue();
    }

    private get analyticalPlanId$(): Observable<number> {
        return this._analyticalPlanId$.asObservable();
    }

    writeValue(value: number): void {
        this.formControl.patchValue(value, {emitEvent: false});
        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.formControl.valid ? null : {analyticalSectionPicker: true});
    }

    setDisabledState(isDisabled: boolean): void {
        this.formControl[isDisabled ? 'disable' : 'enable']({emitEvent: false});
    }

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    private getAnalyticalsPlans(): Observable<any> {
        return this.store.dispatch(new AnalyticalPlanInitAnalyticalParametersAction()).pipe(
            tap(({analyticalPlanState}) => {
                this.analyticalPlansAndSections = analyticalPlanState.analyticalPlansAndSections || [];
            })
        );
    }

    private initSubscription(): void {
        this.watch$ = this.analyticalPlanId$.pipe(
            tap(() => {
                const planAndSectionList = this.analyticalPlansAndSections
                    .find(analyticalPlanAndSections => analyticalPlanAndSections.plan.Id === this.analyticalPlanId);

                this.planAndSections = planAndSectionList || null;
                if(!!this.planAndSections?.sections) {
                    this.planAndSections = {
                        ...this.planAndSections,
                        sections: planAndSectionList.sections.filter(s => s.IdPlanAnalytique === this.analyticalPlanId)
                    };
                }
            })
        );

        this.watch$ = this.formControl.valueChanges.pipe(
            tap(formValues => this.notifyUpdate(formValues))
        );
    }

    ngOnInit(): void {
        this.loading = true;
        this.watch$ = this.getAnalyticalsPlans().pipe(
            tap(() => {
                this.planAndSections = this.analyticalPlansAndSections
                    .find(analyticalPlanAndSections => analyticalPlanAndSections.plan.Id === this.analyticalPlanId) || null;

                this.initSubscription();

                if (this.planAndSections?.sections?.length === 1) {
                    this.formControl.patchValue(this.planAndSections?.sections[0]?.Id);
                }

                this.loading = false;
            })
        );
    }

}
